import CalendarUtils from 'services/CalendarUtils';
import moment from 'moment-timezone';
import PerformanceUtils from 'services/PerformanceUtils';
import { ClinicInfo, Physician } from 'generated/api';

declare const window: Window & { google?: any };
const google = (window as any).google;

interface ClinicHoursDay {
  open?: number | string;
  close?: number | string;
  shut?: boolean;
}

interface ClinicHours {
  [type: string]: ClinicHoursDay[];
}

interface QueueItem {
  room?: string;
  physician_id?: string;
  date?: number;
  [key: string]: any;
}

class ClinicUtils {
  getInitials(clinic: ClinicInfo | undefined): string {
    const name = clinic?.name;
    if (!name) {
      return '';
    }
    const [, ...names] = name.split(' ');
    return names.map((it) => it[0]).join('');
  }

  /**
   * A closed clinic names the clinic that replaced it (forward_to_clinic_id).
   * `hide` alone is not closure: new clinics are hidden until launch.
   */
  isClosed(clinic: ClinicInfo | undefined): boolean {
    return !!clinic?.forward_to_clinic_id;
  }

  /** Clinics a patient can be pointed at: neither hidden nor closed. */
  getOpenClinics(clinics: ClinicInfo[] = []): ClinicInfo[] {
    return clinics.filter((clinic) => !clinic.hide && !this.isClosed(clinic));
  }

  /**
   * The clinic to show when a patient has none of their own: the first open
   * clinic, else the first clinic. Mirrors the backend's getFallbackClinic.
   */
  getFallbackClinic(clinics: ClinicInfo[] = []): ClinicInfo | undefined {
    return this.getOpenClinics(clinics)[0] ?? clinics[0];
  }

  /**
   * Options for a staff selector that should not offer closed clinics. The
   * currently selected clinic is kept even when closed so an existing record
   * still shows where it is. Hidden (pre-launch) clinics stay choosable.
   */
  getSelectableClinics(clinics: ClinicInfo[] = [], selectedId?: string): ClinicInfo[] {
    return clinics.filter((clinic) => !this.isClosed(clinic) || clinic.id === selectedId);
  }

  /**
   * Where a clinic effectively is: a closed clinic resolves through its
   * forward_to_clinic_id to the clinic that replaced it. Anything else,
   * including an unknown id or a forward to a missing clinic, resolves to
   * itself (or to the last clinic reached).
   */
  resolveForwardedClinicId(
    clinicId: string | undefined,
    clinics: ClinicInfo[] = [],
  ): string | undefined {
    let current = clinicId;
    const seen = new Set<string>();
    while (current && !seen.has(current)) {
      seen.add(current);
      const next = clinics.find(({ id }) => id === current)?.forward_to_clinic_id;
      if (!next || !clinics.some(({ id }) => id === next)) break;
      current = next;
    }
    return current;
  }

  getBusyRooms(queue: QueueItem[]): string[] {
    return queue.filter((item) => !!item.room).map((item) => item.room!);
  }

  getAddress(clinic: Partial<ClinicInfo> = {}): string {
    let address = clinic.address || ({} as any);
    let fields = [
      address.line1,
      address.line2,
      address.postcode,
      address.city,
      address.state,
      address.country,
    ].filter((field: string | undefined) => !!field);
    return fields.join(', ');
  }

  getAddressAsObject(clinic: Partial<ClinicInfo> = {}): any {
    return clinic.address || {};
  }

  getLatLng(clinic: Partial<ClinicInfo> = {}): { latitude?: string; longitude?: string } {
    let { latitude, longitude } = (clinic.address || {}) as any;
    return { latitude, longitude };
  }

  getGoogleLatLng(clinic: Partial<ClinicInfo> = {}): any {
    let address = clinic.address as any;
    if (!!address?.latitude && !!address?.longitude && google) {
      return new google.maps.LatLng({
        lat: parseFloat(address.latitude),
        lng: parseFloat(address.longitude),
      });
    }
  }

  getPhone(clinic: Partial<ClinicInfo> = {}): string | undefined {
    return clinic.phone;
  }

  getFax(clinic: Partial<ClinicInfo> = {}): string | undefined {
    return clinic.fax;
  }

  getLifeLabsContractNumber(): string {
    return officeContact.lifelabs_contract;
  }

  getEmail(clinic: Partial<ClinicInfo> = {}): string | undefined {
    return clinic.email;
  }

  isOpenNow(hours: ClinicHours, type: string = 'video'): boolean {
    return !this.isClosedNow(hours, type);
  }

  isClosedNow(hours: ClinicHours, type: string = 'video'): boolean {
    const FORMAT = 'h:mm A';

    if (!this.isOpenToday(hours, type)) return true;

    let opening = this.getOpeningHours(hours, type);
    let closing = this.getClosingHours(hours, type);
    let now = CalendarUtils.format(moment().unix(), FORMAT);

    return (
      moment(now, FORMAT).isAfter(moment(closing, FORMAT)) ||
      moment(now, FORMAT).isBefore(moment(opening, FORMAT))
    );
  }

  isOpenToday(hours: ClinicHours, type: string = 'video', day: number = this.getToday()): boolean {
    const h = this.getHours(hours, type, day);
    return h.shut !== true;
  }

  getOpeningHours(
    hours: ClinicHours,
    type: string = 'video',
    day: number = this.getToday(),
  ): string {
    const h = this.getHours(hours, type, day);
    if (h.shut) return 'Closed';
    return CalendarUtils.formatMilitaryTime(h.open!);
  }

  getClosingHours(
    hours: ClinicHours,
    type: string = 'video',
    day: number = this.getToday(),
  ): string {
    const h = this.getHours(hours, type, day);
    if (h.shut) return 'Closed';
    return CalendarUtils.formatMilitaryTime(h.close!);
  }

  getToday(): number {
    let day = moment().day() - 1;
    if (day < 0) day = 7 - 1;
    return day;
  }

  getHours(hours: ClinicHours, type: string = 'video', day: number = 0): ClinicHoursDay {
    if (hours) return hours[type]?.[day] ?? {};
    return {};
  }

  filterQueuePatients(
    queue: QueueItem[] = [],
    role: string,
    ownId: string,
    physicians: Record<string, Physician> = {},
  ): QueueItem[] {
    if (role === 'physician') {
      queue = queue.filter((element) => {
        return (
          element.physician_id === undefined ||
          element.physician_id === ownId ||
          physicians[ownId]?.intern_of === element.physician_id ||
          (element.physician_id === physicians[element.physician_id]?.id &&
            physicians[element.physician_id]?.intern_of === ownId)
        );
      });
    }
    return PerformanceUtils.sortBy(queue, ['date'], false);
  }

  prepareHours(hours: ClinicHours = {} as ClinicHours): ClinicHours {
    let result: any = PerformanceUtils.clone(hours);
    for (let type in result) {
      for (let index in result[type]) {
        if (!result[type][index].open || !result[type][index].close)
          result[type][index] = { shut: true };
        else {
          result[type][index].open = parseInt(result[type][index].open);
          result[type][index].close = parseInt(result[type][index].close);
          delete result[type][index].shut;
        }
      }
    }
    return result;
  }
}

//TODO multiclinic
const officeContact = {
  lifelabs_contract: 'LifeLabs Contract #: W-704',
};

export default new ClinicUtils();
